import { get } from 'lodash-es';
import { useMemo, type ReactNode } from 'react';
import { defineMessages } from 'react-intl';
import { useTheme } from 'styled-components';
import { getIntlKey, useStrategiesContext } from '../../contexts';
import { useIntl, useOrderIncrements } from '../../hooks';
import { parseColor } from '../../styles';
import { SideEnum, type CustomerOrder, type Order, type Security } from '../../types';
import { getPricingReference, isKeyIn, isOrderComplete, resolvePricingModeDisplaySpecs } from '../../utils';
import { InlineFormattedNumber } from '../FormattedNumber';
import { Icon, IconName } from '../Icons';
import { PriceInlineFormatted } from '../SmartSummary';
import { Text } from '../Text';
import { MinTd, OrderSide, Summary, Table, Tbody, Td } from './styles';

interface OrderCardSummaryProps extends Partial<
  Pick<
    Order,
    | 'PricingReference'
    | 'PricingMode'
    | 'Allocation'
    | 'OrderQty'
    | 'Currency'
    | 'OrdStatus'
    | 'Price'
    | 'CumQty'
    | 'AvgPxAllIn'
    | 'AvgPx'
    | 'Strategy'
    | 'Parameters'
  >
> {
  OrdStatus: Order['OrdStatus'];
  Side: Order['Side'];
  security: Security;
  showFees: boolean;
  customerOrder?: CustomerOrder;
  isCustomerOrderCard?: boolean;
  showGroup?: boolean;

  tableDetails?: { id: string; label: ReactNode; value: ReactNode }[];

  subAccountDetails?: ReactNode;
}

const messages = defineMessages({
  filled: {
    id: 'OrderCard.filled',
    defaultMessage: 'Filled',
  },
  customerFilled: {
    id: 'OrderCard.customerFilled',
    defaultMessage: 'Customer Filled',
  },
  buy: {
    defaultMessage: 'Buy',
    id: 'OrderCard.buy',
  },
  sell: {
    defaultMessage: 'Sell',
    id: 'OrderCard.sell',
  },
  customer: {
    defaultMessage: 'Customer',
    id: 'OrderCard.customer',
  },
});

const OrderSummaryLabelMode = {
  Customer: 'customer',
  Order: 'order',
} as const;
type OrderSummaryLabelMode = (typeof OrderSummaryLabelMode)[keyof typeof OrderSummaryLabelMode];

interface OrderSummaryRowProps {
  labelMode: OrderSummaryLabelMode;
  Side: Order['Side'];
  quantity?: Order['OrderQty'];
  filledQuantity?: Order['CumQty'];
  price?: Order['Price'];
  averagePrice?: Order['AvgPx'];
  Currency: string;
  PricingMode?: Order['PricingMode'];
  PricingReference?: Order['PricingReference'];
  security: Security;
  showFees: boolean;
  endPriceToDisplay?: string;
  orderIncrements: ReturnType<typeof useOrderIncrements>;
  orderPriceDisplaySpec: ReturnType<typeof resolvePricingModeDisplaySpecs>['entryPrice']['priceDisplaySpec'];
  priceCurrency: string | undefined;
  priceIncrement: ReturnType<typeof resolvePricingModeDisplaySpecs>['fillPrice']['priceIncrement'];
  priceDisplaySpec: ReturnType<typeof resolvePricingModeDisplaySpecs>['fillPrice']['priceDisplaySpec'];
  toDisplayPrice: ReturnType<typeof resolvePricingModeDisplaySpecs>['fillPrice']['toDisplayPrice'];
  inlineFormattedNumberBg: string | undefined;
}

function OrderSummaryRow({
  labelMode,
  Side,
  quantity,
  filledQuantity,
  price,
  averagePrice,
  Currency,
  PricingMode,
  PricingReference,
  security,
  showFees,
  endPriceToDisplay,
  orderIncrements,
  orderPriceDisplaySpec,
  priceCurrency,
  priceIncrement,
  priceDisplaySpec,
  toDisplayPrice,
  inlineFormattedNumberBg,
}: OrderSummaryRowProps) {
  const { formatMessage } = useIntl();
  const useCustomerLabels = labelMode === OrderSummaryLabelMode.Customer;
  const sideIntlKey = getIntlKey(Side);
  const translatedSide = !Side ? null : isKeyIn(sideIntlKey, messages) ? formatMessage(messages[sideIntlKey]) : Side;

  return (
    <>
      <tr>
        <Td>
          <OrderSide isBuy={Side === SideEnum.Buy} isSell={Side === SideEnum.Sell}>
            {(useCustomerLabels ? `${formatMessage(messages.customer)} ` : '') + translatedSide}
          </OrderSide>
        </Td>
        <MinTd>
          <InlineFormattedNumber
            number={quantity}
            increment={orderIncrements.quantityIncrement}
            specification={orderPriceDisplaySpec}
            currency={Currency}
            background={inlineFormattedNumberBg}
          />
        </MinTd>
        <MinTd>
          <Text color="colorTextSubtle">
            <Icon icon={IconName.AtSymbol} />
          </Text>
        </MinTd>
        <MinTd>
          {price ? (
            <PriceInlineFormatted
              showPriceReference
              entity={{
                PricingMode,
                PricingReference,
              }}
              price={price}
              endPrice={endPriceToDisplay}
              security={security}
            />
          ) : (
            <InlineFormattedNumber
              number={getPricingReference({ PricingMode, PricingReference })?.value}
              currency={getPricingReference({ PricingMode, PricingReference })?.currency}
              align="right"
            />
          )}
        </MinTd>
      </tr>
      <tr>
        <Td>{useCustomerLabels ? formatMessage(messages.customerFilled) : formatMessage(messages.filled)}</Td>
        <MinTd data-testid="order-card-summary-filled">
          <InlineFormattedNumber
            number={filledQuantity}
            increment={orderIncrements.quantityIncrement}
            specification={orderPriceDisplaySpec}
            currency={Currency}
            background={inlineFormattedNumberBg}
          />
        </MinTd>
        <MinTd>
          <Text color="colorTextSubtle">
            <Icon icon={IconName.AtSymbol} />
          </Text>
        </MinTd>
        <MinTd data-testid="order-card-summary-filled-price">
          <InlineFormattedNumber
            number={toDisplayPrice(averagePrice)}
            increment={priceIncrement}
            specification={priceDisplaySpec}
            currency={priceCurrency}
            background={inlineFormattedNumberBg}
            startIcon={showFees ? IconName.Fees : undefined}
            align="right"
          />
        </MinTd>
      </tr>
    </>
  );
}

export const OrderCardSummary = (props: OrderCardSummaryProps) => {
  const {
    Side,
    OrderQty,
    Currency = '',
    OrdStatus,
    Price,
    CumQty,
    AvgPxAllIn,
    AvgPx,
    Strategy,
    Parameters,
    security,
    showFees,
    customerOrder,
    isCustomerOrderCard,
    subAccountDetails,
    tableDetails,
  } = props;

  const orderIncrements = useOrderIncrements({
    Currency,
    Symbol: security.Symbol,
  });

  const priceDisplaySpecs = resolvePricingModeDisplaySpecs({
    pricingMode: props.PricingMode,
    security,
  });
  const { priceDisplaySpec: orderPriceDisplaySpec } = priceDisplaySpecs.entryPrice;
  const { priceCurrency, priceIncrement, priceDisplaySpec, toDisplayPrice } = priceDisplaySpecs.fillPrice;

  const theme = useTheme();
  const inlineFormattedNumberBg = parseColor(theme, isOrderComplete(OrdStatus) ? 'gray.010' : 'gray.main');

  const renderedTableDetails = useMemo(() => {
    return tableDetails?.map(detail => (
      <tr key={detail.id}>
        <Td>{detail.label}</Td>
        <MinTd />
        <MinTd />
        <MinTd style={{ color: theme.colorTextImportant }}>{detail.value}</MinTd>
      </tr>
    ));
  }, [tableDetails, theme.colorTextImportant]);

  const { strategiesByName } = useStrategiesContext();

  const endPriceToDisplay = useMemo<string | undefined>(() => {
    if (Strategy && strategiesByName.has(Strategy)) {
      const strat = strategiesByName.get(Strategy);
      if (strat?.showPriceAsRange && strat.endPriceParamName) {
        return get(Parameters, strat.endPriceParamName);
      }
    }
    return undefined;
  }, [Strategy, strategiesByName, Parameters]);

  const sharedRowProps = {
    Side,
    Currency,
    PricingMode: props.PricingMode,
    PricingReference: props.PricingReference,
    security,
    showFees,
    endPriceToDisplay,
    orderIncrements,
    orderPriceDisplaySpec,
    priceCurrency,
    priceIncrement,
    priceDisplaySpec,
    toDisplayPrice,
    inlineFormattedNumberBg,
  };

  return (
    <Summary>
      <Table>
        <Tbody>
          {customerOrder && (
            <OrderSummaryRow
              {...sharedRowProps}
              labelMode={OrderSummaryLabelMode.Customer}
              quantity={customerOrder.OrderQty}
              filledQuantity={customerOrder.CumQty}
              price={customerOrder.Price}
              averagePrice={customerOrder.AvgPx}
            />
          )}
          <OrderSummaryRow
            {...sharedRowProps}
            labelMode={isCustomerOrderCard ? OrderSummaryLabelMode.Customer : OrderSummaryLabelMode.Order}
            quantity={OrderQty}
            filledQuantity={CumQty}
            price={Price}
            averagePrice={showFees ? AvgPxAllIn || AvgPx : AvgPx}
          />
          {renderedTableDetails}
        </Tbody>
      </Table>
      <>{subAccountDetails}</>
    </Summary>
  );
};
